// useBookingListInterval.js
import { useEffect, useRef, useCallback, useState } from 'react';
import moment from 'moment-timezone';
import {EARLY_CHECKIN_DURATION} from "../../constants/default";
import {getBookingList, getConfig, setBookingList, setCurrentBooking} from "../../reducers/configSlice";
import {useDispatch, useSelector} from "react-redux";
import {STORAGE} from "../storage";

// Helper function for deep equality check
const isEqual = (obj1, obj2) => {
    if (!obj1 || !obj2) return obj1 === obj2;
    
    // Compare booking IDs for simple equality check
    if (obj1.booking_id && obj2.booking_id) {
        return obj1.booking_id === obj2.booking_id && 
               obj1.current === obj2.current && 
               obj1.early === obj2.early;
    }
    
    return false;
};

// Helper function to check if booking lists have the same current bookings
const haveSameCurrentBookings = (list1, list2) => {
    if (!list1 || !list2) return false;
    
    const current1 = list1.filter(b => b.current);
    const current2 = list2.filter(b => b.current);
    
    // console.log('[BOOKING_INTERVAL] Comparing current bookings:', {
    //     list1CurrentCount: current1.length,
    //     list2CurrentCount: current2.length,
    //     list1CurrentIds: current1.map(b => b.booking_id),
    //     list2CurrentIds: current2.map(b => b.booking_id)
    // });
    
    if (current1.length !== current2.length) {
        // console.log('[BOOKING_INTERVAL] Current booking counts differ, lists are different');
        return false;
    }
    
    // If both lists have no current bookings, they're effectively the same
    if (current1.length === 0 && current2.length === 0) {
        // console.log('[BOOKING_INTERVAL] Both lists have no current bookings, considered same');
        return true;
    }
    
    const result = current1.every(b1 => 
        current2.some(b2 => b1.booking_id === b2.booking_id && b1.early === b2.early)
    );
    
    // console.log('[BOOKING_INTERVAL] Current booking comparison result:', result);
    return result;
};

const useBookingListInterval = () => {
    const config = useSelector(getConfig);
    const bookingList = useSelector(getBookingList);
    const dispatch = useDispatch();

    // State to track if Soketi is being used
    const [isSoketiActive, setIsSoketiActive] = useState(false);

    // Use refs to track previous state and prevent infinite loops
    const prevBookingListRef = useRef([]);
    const currentBookingRef = useRef(null);
    const isProcessingRef = useRef(false);
    const intervalRef = useRef(null);

    // Check if Soketi is active on mount and when needed
    useEffect(() => {
        const checkSoketiStatus = async () => {
            const soketiUrl = await STORAGE.get("soketiUrl");
            setIsSoketiActive(!!soketiUrl);
        };
        checkSoketiStatus();
    }, []);

    // Define clearCurrentBooking with useCallback so it can be used as a dependency
    const clearCurrentBooking = useCallback(() => {
        // Skip if there's already no current booking
        if (currentBookingRef.current === null) {
            // console.log('[BOOKING_INTERVAL] Skipping clear - no current booking to clear');
            return;
        }
        
        // console.log('[BOOKING_INTERVAL] Clearing current booking');
        
        // Set processing flag to prevent loops
        isProcessingRef.current = true;
        
        try {
            const hasCurrentBookings = bookingList.some(booking => booking.current);

            if (hasCurrentBookings) {
                // console.log('[BOOKING_INTERVAL] Clearing current booking flag from all bookings');
                const newBookingList = bookingList.map(booking => {
                    if (booking.current) {
                        // console.log('[BOOKING_INTERVAL] Removing current flag from booking:', booking.booking_id);
                        return { ...booking, current: false, early: false };
                    }
                    return booking;
                });
                
                // console.log('[BOOKING_INTERVAL] Dispatching updated booking list with no current bookings');
                // Update our ref before dispatching to prevent loops
                currentBookingRef.current = null;
                
                // Dispatch both updates to ensure state is fully cleared
                dispatch(setBookingList(newBookingList));
                dispatch(setCurrentBooking(null));
            } else {
                // console.log('[BOOKING_INTERVAL] No bookings marked as current, just clearing currentBooking');
                // Update our ref before dispatching to prevent loops
                currentBookingRef.current = null;
                dispatch(setCurrentBooking(null));
            }
        } finally {
            // Always clear processing flag
            setTimeout(() => {
                // console.log('[BOOKING_INTERVAL] Clearing processing flag after update');
                isProcessingRef.current = false;
            }, 0);
        }
    }, [bookingList, dispatch]);

    useEffect(() => {
        // Skip this entire hook if Soketi is active - Soketi has its own booking interval
        if (isSoketiActive) {
            // console.log('[BOOKING_INTERVAL] Soketi is active, skipping Socket.IO booking interval');
            return;
        }

        // console.log('[BOOKING_INTERVAL] Effect triggered with bookingList length:', bookingList.length);

        // Skip if we're already processing an update to prevent loops
        if (isProcessingRef.current) {
            // console.log('[BOOKING_INTERVAL] Skipping effect execution - already processing an update');
            return;
        }
        
        // Skip if the booking list hasn't actually changed in a meaningful way
        if (haveSameCurrentBookings(bookingList, prevBookingListRef.current)) {
            // console.log('[BOOKING_INTERVAL] Skipping effect - booking list current status unchanged');
            return;
        }
        
        // console.log('[BOOKING_INTERVAL] Config:', JSON.stringify({
        //     timezone: config.timezone,
        //     granularity: config.granularity,
        //     has_checkin: config.has_checkin
        // }));
        
        // Store the current booking list for future comparison
        prevBookingListRef.current = [...bookingList];
        
        const findCurrentBooking = () => {
            const currentMoment = moment().tz(config.timezone);
            // console.log('[BOOKING_INTERVAL] Finding current booking at:', currentMoment.format('YYYY-MM-DD HH:mm:ss.SSS'));
            
            if (!bookingList.length) {
                // console.log('[BOOKING_INTERVAL] No bookings in list to check');
                return null;
            }
            
            // console.log('[BOOKING_INTERVAL] Checking through', bookingList.length, 'bookings');
            
            const current = bookingList.find(booking => {
                const startMoment = moment.tz(booking.start, config.timezone);
                const endMoment = moment.tz(booking.end, config.timezone);
                
                // console.log('[BOOKING_INTERVAL] Evaluating booking:', {
                //     id: booking.booking_id,
                //     title: booking.title || 'No title',
                //     start: startMoment.format('YYYY-MM-DD HH:mm:ss'),
                //     end: endMoment.format('YYYY-MM-DD HH:mm:ss'),
                //     current: booking.current,
                //     early: booking.early,
                //     currentTime: currentMoment.format('YYYY-MM-DD HH:mm:ss')
                // });
                
                // Use '[)' inclusivity parameter to include start time but exclude end time
                const isBetween = currentMoment.isBetween(startMoment, endMoment, undefined, '[)');
                
                // Additional time checks for debugging
                const isAfterStart = currentMoment.isAfter(startMoment) || currentMoment.isSame(startMoment);
                const isBeforeEnd = currentMoment.isBefore(endMoment);
                
                const condition = (!booking.current || (booking.current && booking.early));
                const result = isBetween && condition;
                
                // console.log('[BOOKING_INTERVAL] Booking evaluation details:', {
                //     isBetween,
                //     isAfterStart,
                //     isBeforeEnd,
                //     condition,
                //     result,
                //     currentTime: currentMoment.format('HH:mm:ss'),
                //     startTime: startMoment.format('HH:mm:ss'),
                //     endTime: endMoment.format('HH:mm:ss')
                // });
                
                return result;
            });
            
            // console.log('[BOOKING_INTERVAL] Current booking found:', current ? {
            //     id: current.booking_id,
            //     title: current.title || 'No title',
            //     start: moment.tz(current.start, config.timezone).format('YYYY-MM-DD HH:mm:ss'),
            //     end: moment.tz(current.end, config.timezone).format('YYYY-MM-DD HH:mm:ss')
            // } : 'None');
            
            return current;
        };

        const findEarlyBooking = () => {
            const currentMoment = moment().tz(config.timezone);
            // console.log('[BOOKING_INTERVAL] Finding early booking at:', currentMoment.format('YYYY-MM-DD HH:mm:ss.SSS'));

            if (!bookingList.length) {
                // console.log('[BOOKING_INTERVAL] No bookings in list to check for early booking');
                return null;
            }
            
            // console.log('[BOOKING_INTERVAL] Early check duration:', EARLY_CHECKIN_DURATION, 'minutes');
            
            const early = bookingList.find(booking => {
                const startMoment = moment.tz(booking.start, config.timezone).subtract(EARLY_CHECKIN_DURATION, 'minutes');
                const endMoment = moment.tz(booking.end, config.timezone);
                
                // console.log('[BOOKING_INTERVAL] Evaluating for early booking:', {
                //     id: booking.booking_id,
                //     title: booking.title || 'No title',
                //     earlyStart: startMoment.format('YYYY-MM-DD HH:mm:ss'),
                //     actualStart: moment.tz(booking.start, config.timezone).format('YYYY-MM-DD HH:mm:ss'),
                //     end: endMoment.format('YYYY-MM-DD HH:mm:ss'),
                //     current: booking.current
                // });
                
                // Use '[)' inclusivity parameter for early booking check
                const isBetween = currentMoment.isBetween(startMoment, endMoment, undefined, '[)');
                const notCurrent = !booking.current;
                const result = isBetween && notCurrent;
                
                // console.log('[BOOKING_INTERVAL] Early booking evaluation:', {
                //     isBetween,
                //     notCurrent,
                //     result
                // });
                
                return result;
            });
            
            // console.log('[BOOKING_INTERVAL] Early booking found:', early ? {
            //     id: early.booking_id,
            //     title: early.title || 'No title',
            //     start: moment.tz(early.start, config.timezone).format('YYYY-MM-DD HH:mm:ss'),
            //     end: moment.tz(early.end, config.timezone).format('YYYY-MM-DD HH:mm:ss')
            // } : 'None');
            
            return early;
        };

        const updateBookingListState = (current) => {
            // Skip if this booking is already set as current
            if (isEqual(currentBookingRef.current, current)) {
                // console.log('[BOOKING_INTERVAL] Skipping update - current booking unchanged:', current.booking_id);
                return;
            }
            
            // console.log('[BOOKING_INTERVAL] Updating booking list state for current booking:', {
            //     id: current.booking_id,
            //     title: current.title || 'No title'
            // });
            
            // Set processing flag to prevent loops
            isProcessingRef.current = true;
            
            try {
                const newBookingList = bookingList.map(booking => {
                    if (booking.booking_id === current.booking_id) {
                        // console.log('[BOOKING_INTERVAL] Setting as current booking:', booking.booking_id);
                        return { ...booking, current: true, early: false, next: false };
                    } else if (booking.current) {
                        // console.log('[BOOKING_INTERVAL] Removing current flag from booking:', booking.booking_id);
                        return { ...booking, current: false };
                    } else {
                        return booking;
                    }
                });

                // console.log('[BOOKING_INTERVAL] Dispatching updated booking list');
                
                // Update our ref before dispatching to prevent loops
                currentBookingRef.current = { ...current, current: true, early: false, next: false };
                
                // Batch updates to reduce renders
                dispatch(setBookingList(newBookingList));
                dispatch(setCurrentBooking(currentBookingRef.current));
            } finally {
                // Always clear processing flag
                setTimeout(() => {
                    isProcessingRef.current = false;
                }, 0);
            }
        };

        const updateForEarlyBooking = (earlyBooking) => {
            // Skip if this booking is already set as early
            const isAlreadyEarly = isEqual(currentBookingRef.current, { ...earlyBooking, current: true, early: true });
            if (isAlreadyEarly) {
                // console.log('[BOOKING_INTERVAL] Skipping update - early booking unchanged:', earlyBooking.booking_id);
                return;
            }
            
            // console.log('[BOOKING_INTERVAL] Updating for early booking:', {
            //     id: earlyBooking.booking_id,
            //     title: earlyBooking.title || 'No title'
            // });
            
            // Set processing flag to prevent loops
            isProcessingRef.current = true;
            
            try {
                const newBookingList = bookingList.map(booking => {
                    if (booking.booking_id === earlyBooking.booking_id) {
                        // console.log('[BOOKING_INTERVAL] Setting as early booking:', booking.booking_id);
                        return { ...booking, current: true, early: true, next: false };
                    } else if (booking.current) {
                        // console.log('[BOOKING_INTERVAL] Removing current flag from booking:', booking.booking_id);
                        return { ...booking, current: false };
                    } else {
                        return booking;
                    }
                });

                // console.log('[BOOKING_INTERVAL] Dispatching updated booking list for early booking');
                
                // Update our ref before dispatching to prevent loops
                currentBookingRef.current = { ...earlyBooking, current: true, early: true, next: false };
                
                // Batch updates to reduce renders
                dispatch(setBookingList(newBookingList));
                dispatch(setCurrentBooking(currentBookingRef.current));
            } finally {
                // Always clear processing flag
                setTimeout(() => {
                    isProcessingRef.current = false;
                }, 0);
            }
        };

        const updateBookingList = () => {
            // console.log('[BOOKING_INTERVAL] Updating booking list, current time:', moment().tz(config.timezone).format('YYYY-MM-DD HH:mm:ss.SSS'));
            
            if (!bookingList.length) {
                // console.log('[BOOKING_INTERVAL] Booking list is empty, skipping update');
                return;
            }

            // console.log('[BOOKING_INTERVAL] Current bookings in state:', bookingList.filter(b => b.current).length);
            
            const current = findCurrentBooking();
            if (current) {
                // console.log('[BOOKING_INTERVAL] Found current booking, updating state');
                updateBookingListState(current);
            } else {
                // console.log('[BOOKING_INTERVAL] No current booking found');
                
                // If no current booking is found, we should clear the current booking
                const hasCurrentBookings = bookingList.some(booking => booking.current);
                // console.log('[BOOKING_INTERVAL] Has bookings marked as current:', hasCurrentBookings);
                
                // Force clear current booking regardless of state to ensure it's properly cleared
                clearCurrentBooking();
                
                if (config.granularity <= 15 && config.has_checkin) {
                    // console.log('[BOOKING_INTERVAL] Checking for early bookings (granularity <= 15 and has_checkin)');
                    const earlyBooking = findEarlyBooking();
                    if (earlyBooking) {
                        // console.log('[BOOKING_INTERVAL] Found early booking, updating state');
                        updateForEarlyBooking(earlyBooking);
                    } else {
                        // console.log('[BOOKING_INTERVAL] No early booking found');
                    }
                } else {
                    // console.log('[BOOKING_INTERVAL] Skipping early booking check:', {
                    //     granularity: config.granularity,
                    //     has_checkin: config.has_checkin
                    // });
                }
            }
        };

        // Setup interval for periodic updates
        const setupInterval = () => {
            // console.log('[BOOKING_INTERVAL] Setting up interval for booking list updates (every 3 seconds)');
            
            // Clear any existing interval
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            
            // Create new interval
            intervalRef.current = setInterval(() => {
                // console.log('[BOOKING_INTERVAL] Interval triggered at:', moment().tz(config.timezone).format('YYYY-MM-DD HH:mm:ss.SSS'));
                
                // Skip if we're already processing an update
                if (isProcessingRef.current) {
                    // console.log('[BOOKING_INTERVAL] Skipping interval update - already processing');
                    return;
                }
                
                // Check if we need to force an update due to time boundaries
                const currentMoment = moment().tz(config.timezone);
                let forceUpdate = false;
                
                // If there's a current booking, check if we've passed its end time
                if (currentBookingRef.current) {
                    const endMoment = moment.tz(currentBookingRef.current.end, config.timezone);
                    if (currentMoment.isAfter(endMoment) || currentMoment.isSame(endMoment)) {
                        // console.log('[BOOKING_INTERVAL] Force update - current booking has ended');
                        forceUpdate = true;
                        // Reset processing flag to ensure update happens
                        isProcessingRef.current = false;
                    }
                }
                
                updateBookingList();
            }, 3000); // Run every 3 seconds instead of 5
            
            // Run immediately on mount or when bookingList changes
            // console.log('[BOOKING_INTERVAL] Running initial update immediately');
            updateBookingList();
        };

        if (bookingList.length) {
            setupInterval();
        } else {
            // console.log('[BOOKING_INTERVAL] No bookings in list, not setting up interval');
            
            // Clear any existing interval
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            
            // Clear current booking if needed
            if (currentBookingRef.current !== null) {
                clearCurrentBooking();
            }
        }

        // Cleanup function
        return () => {
            // console.log('[BOOKING_INTERVAL] Cleaning up interval');
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [bookingList, dispatch, config, clearCurrentBooking, EARLY_CHECKIN_DURATION, isSoketiActive]);

    // Add a separate effect to force updates at booking boundaries
    useEffect(() => {
        // Skip this boundary check if Soketi is active - Soketi has its own boundary checking
        if (isSoketiActive) {
            // console.log('[BOOKING_INTERVAL] Soketi is active, skipping Socket.IO boundary check');
            return;
        }

        // Create an interval that runs every minute to check for booking boundaries
        const boundaryCheckInterval = setInterval(() => {
            const currentMoment = moment().tz(config.timezone);
            // console.log('[BOOKING_INTERVAL] Boundary check at:', currentMoment.format('YYYY-MM-DD HH:mm:ss'));
            
            // If there's a current booking, check if we've passed its end time
            if (currentBookingRef.current) {
                const endMoment = moment.tz(currentBookingRef.current.end, config.timezone);
                if (currentMoment.isAfter(endMoment) || currentMoment.isSame(endMoment)) {
                    // console.log('[BOOKING_INTERVAL] Boundary check: Current booking has ended, forcing update');
                    // Reset processing flag and force an update
                    isProcessingRef.current = false;
                    // Force clear current booking
                    clearCurrentBooking();
                }
            }
        }, 60000); // Check every minute
        
        return () => {
            clearInterval(boundaryCheckInterval);
        };
    }, [config.timezone, clearCurrentBooking, isSoketiActive]); // Use clearCurrentBooking as a dependency instead of bookingList

    return null;
};

export default useBookingListInterval;
